/**
 * ADA Global Pause Control - WCAG 2.2 Compliant
 * Stops all carousel/slider movement across the site
 */

(function() {
  'use strict';

  const pauseButtons = [];
  let primaryPauseButton = null;

  // Storage key for localStorage
  const STORAGE_KEY = 'ada-global-pause';
  
  // Track all Swiper instances and custom handlers
  const swiperInstances = new Set();
  const customHandlers = new Set();
  
  // Global pause state
  let isPaused = false;

  /**
   * Register a Swiper instance for global pause control
   */
  function registerSwiperInstance(swiper) {
    if (swiper && typeof swiper.autoplay !== 'undefined') {
      swiperInstances.add(swiper);
      
      // If global pause is already active, stop this instance
      if (isPaused && swiper.autoplay && swiper.autoplay.running) {
        swiper.autoplay.stop();
      }
    }
  }

  function registerCustomHandler(handler) {
    if (!handler || typeof handler.pause !== 'function' || typeof handler.resume !== 'function') {
      return;
    }
    customHandlers.add(handler);
    if (isPaused) {
      try { handler.pause(); } catch (e) { console.warn('Custom pause handler error:', e); }
    }
  }

  function unregisterCustomHandler(handler) {
    customHandlers.delete(handler);
  }

  /**
   * Unregister a Swiper instance
   */
  function unregisterSwiperInstance(swiper) {
    swiperInstances.delete(swiper);
  }

  /**
   * Pause all carousels
   */
  function pauseAllCarousels() {
    isPaused = true;
    swiperInstances.forEach(swiper => {
      if (swiper && swiper.autoplay && swiper.autoplay.running) {
        swiper.autoplay.stop();
      }
    });
    customHandlers.forEach(handler => {
      try { handler.pause(); } catch (e) { console.warn('Custom pause handler error:', e); }
    });
    savePauseState(true);
    announcePauseChange(true);
  }

  /**
   * Resume all carousels
   */
  function resumeAllCarousels() {
    isPaused = false;
    swiperInstances.forEach(swiper => {
      if (swiper && swiper.autoplay && !swiper.autoplay.running) {
        swiper.autoplay.start();
      }
    });
    customHandlers.forEach(handler => {
      try { handler.resume(); } catch (e) { console.warn('Custom resume handler error:', e); }
    });
    savePauseState(false);
    announcePauseChange(false);
  }

  /**
   * Toggle pause state
   */
  function togglePause() {
    if (isPaused) {
      resumeAllCarousels();
    } else {
      pauseAllCarousels();
    }
    updatePauseButtons();
  }

  /**
   * Save pause state to localStorage
   */
  function savePauseState(paused) {
    try {
      localStorage.setItem(STORAGE_KEY, paused ? 'true' : 'false');
    } catch (e) {
      console.warn('Could not save pause state to localStorage:', e);
    }
  }

  /**
   * Load pause state from localStorage
   */
  function loadPauseState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'true';
    } catch (e) {
      console.warn('Could not load pause state from localStorage:', e);
      return false;
    }
  }

  /**
   * Create pause toggle button
   */
  function createPauseButton(id = 'ada-global-pause-toggle') {
    const button = document.createElement('button');
    button.type = 'button';
    if (id) {
      button.id = id;
    }
    button.className = 'ada-global-pause-toggle';
    
    // Check if this is for mobile menu
    const isMobileMenu = button.dataset && button.dataset.context === 'mobile-menu';
    
    // Enhanced aria-label for ADA compliance
    if (isMobileMenu) {
      button.setAttribute('aria-label', 'Pause all moving content. Currently playing. Press to pause all carousels and animations.');
    } else {
      button.setAttribute('aria-label', 'Pause all carousels');
    }
    
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('title', 'Pause all carousels');
    
    // Add icon and text
    // For mobile menu, use uppercase text to match button style
    const pauseText = isMobileMenu ? 'PAUSE MOVEMENT' : 'Pause';
    
    button.innerHTML = `
      <span class="ada-pause-icon" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 3H7V13H5V3ZM9 3H11V13H9V3Z" fill="currentColor"/>
        </svg>
      </span>
      <span class="ada-pause-text">${pauseText}</span>
    `;
    
    // Add mobile menu styling class if in mobile menu context
    if (isMobileMenu) {
      button.classList.add('mm-button-style');
    }
    
    return button;
  }

  function registerPauseButton(button, options = {}) {
    if (!button) return;
    if (options.isPrimary) {
      primaryPauseButton = button;
    }
    pauseButtons.push(button);
    button.addEventListener('click', handlePauseButtonClick);
    button.addEventListener('keydown', handlePauseButtonKeydown);
    if (button.dataset && button.dataset.context === 'mobile-menu') {
      button.classList.add('ada-toggle-visible', 'w-full');
    }
  }

  function handlePauseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    togglePause();
  }

  function handlePauseButtonKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      togglePause();
    }
  }

  /**
   * Get pause icon SVG - white bars
   */
  function getPauseIcon() {
    return `
      <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" style="fill: #000000; color: #000000;">
        <path d="M5 3H7V13H5V3ZM9 3H11V13H9V3Z" fill="#000000" style="fill: #000000 !important;"/>
      </svg>
    `;
  }

  /**
   * Get play icon SVG - simplified with explicit white fill
   */
  function getPlayIcon() {
    return `
      <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 3L13 8L3 13V3Z" fill="#ffffff" stroke="#ffffff" stroke-width="0"/>
      </svg>
    `;
  }

  /**
   * Update pause button appearance
   */
  function updatePauseButtons() {
    pauseButtons.forEach(updatePauseButtonState);
  }

  function updatePauseButtonState(button) {
    if (!button) return;

    const iconSpan = button.querySelector('.ada-pause-icon');
    const textSpan = button.querySelector('.ada-pause-text');
    
    // Match contrast toggle behavior: active class when "on" (paused), no active class when "off" (playing)
    const isActive = isPaused; // When paused=true, button should be active (black)
    
    button.classList.toggle('active', isActive);
    button.classList.toggle('is-paused', isActive);
    button.setAttribute('aria-pressed', isActive.toString());
    
    // Update aria-label for mobile menu buttons with enhanced description
    const isMobileMenu = button.dataset && button.dataset.context === 'mobile-menu';
    
    if (isPaused) {
      if (isMobileMenu) {
        button.setAttribute('aria-label', 'Resume all moving content. Currently paused. Press to resume all carousels and animations.');
      } else {
        button.setAttribute('aria-label', 'Resume all carousels');
      }
      button.setAttribute('title', 'Resume all carousels');
      if (iconSpan) {
        // Clear and set icon
        iconSpan.innerHTML = '';
        const playSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        playSvg.setAttribute('width', '16');
        playSvg.setAttribute('height', '16');
        playSvg.setAttribute('viewBox', '0 0 16 16');
        // For mobile menu, keep icon black (white background button)
        const iconColor = isMobileMenu ? '#000000' : '#ffffff';
        playSvg.setAttribute('style', 'display: block; width: 16px; height: 16px; fill: ' + iconColor + '; color: ' + iconColor + ';');
        
        const playPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        playPath.setAttribute('d', 'M3 3L13 8L3 13V3Z');
        playPath.setAttribute('fill', iconColor);
        playPath.setAttribute('stroke', iconColor);
        playPath.setAttribute('style', 'fill: ' + iconColor + ' !important; stroke: ' + iconColor + ' !important; display: block !important;');
        
        playSvg.appendChild(playPath);
        iconSpan.appendChild(playSvg);
        
        // Force styles after append - ensure icon doesn't cause button to grow
        setTimeout(() => {
          iconSpan.setAttribute('style', 'display: inline-flex !important; align-items: center !important; visibility: visible !important; opacity: 1 !important; margin-right: 8px !important; flex-shrink: 0 !important; width: auto !important; height: auto !important;');
          playSvg.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 16px !important; height: 16px !important; fill: #000000 !important; color: #000000 !important;');
          playPath.setAttribute('style', 'fill: #000000 !important; stroke: #000000 !important; display: block !important; visibility: visible !important; opacity: 1 !important;');
        }, 10);
      }
      // For mobile menu, keep text as "PAUSE MOVEMENT" regardless of state
      if (textSpan) {
        if (isMobileMenu) {
          textSpan.textContent = 'PAUSE MOVEMENT';
        } else {
          textSpan.textContent = 'Resume';
        }
      }
      } else {
        if (isMobileMenu) {
          button.setAttribute('aria-label', 'Pause all moving content. Currently playing. Press to pause all carousels and animations.');
        } else {
          button.setAttribute('aria-label', 'Pause all carousels');
        }
        button.setAttribute('title', 'Pause all carousels');
      if (iconSpan) {
        iconSpan.innerHTML = getPauseIcon();
        // Ensure pause icon is black when inactive (transparent background)
        setTimeout(() => {
          const svg = iconSpan.querySelector('svg');
          const path = iconSpan.querySelector('path');
          if (svg) {
            svg.setAttribute('style', 'fill: #000000 !important; color: #000000 !important; display: block;');
          }
          if (path) {
            path.setAttribute('fill', '#000000');
            path.setAttribute('style', 'fill: #000000 !important; display: block;');
          }
        }, 0);
      }
      // For mobile menu, keep text as "PAUSE MOVEMENT" regardless of state
      if (textSpan) {
        if (isMobileMenu) {
          textSpan.textContent = 'PAUSE MOVEMENT';
        } else {
          textSpan.textContent = 'Pause';
        }
      }
    }
    
    // CRITICAL: Prevent button from growing - ensure consistent sizing - MATCH LOGIN BUTTON EXACTLY
    // Also ensure text doesn't overflow when toggling between "Pause" and "Resume"
    const isFooter = button.dataset && button.dataset.context === 'footer';
    
    if (isMobileMenu) {
      // Apply ALL styles inline to match login button exactly - override everything
      // Also ensure button has same classes as login button
      if (!button.classList.contains('mm-button')) {
        button.className = 'mm-button uppercase tracking-1_5px font-bold text-center border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-all duration-200 ' + button.className;
      }
      
      // CRITICAL: Set fixed width to prevent size changes when text changes
      // Use max width of "PAUSE MOVEMENT" and "RESUME" to ensure no overflow
      button.style.setProperty('width', '100%', 'important');
      button.style.setProperty('max-width', '100%', 'important');
      button.style.setProperty('min-width', '0', 'important');
      button.style.setProperty('box-sizing', 'border-box', 'important');
      button.style.setProperty('overflow', 'hidden', 'important');
      button.style.setProperty('text-overflow', 'ellipsis', 'important');
      button.style.setProperty('white-space', 'nowrap', 'important');
      
      const buttonStyles = 'width: 100% !important; max-width: 100% !important; height: 44px !important; max-height: 44px !important; min-height: 44px !important; font-size: 14px !important; line-height: 1.2 !important; padding: 10px 16px !important; box-sizing: border-box !important; border-radius: 4px !important; background-color: #ffffff !important; border: 2px solid #000000 !important; color: #000000 !important; font-weight: bold !important; text-transform: uppercase !important; letter-spacing: 1.5px !important; display: flex !important; align-items: center !important; justify-content: center !important; opacity: 1 !important; visibility: visible !important; text-decoration: none !important; cursor: pointer !important;';
      button.style.cssText = buttonStyles;
      
      // Also apply to text and icon to ensure they don't cause size issues
      if (textSpan) {
        textSpan.style.cssText = 'font-size: 14px !important; line-height: 1.2 !important; font-weight: bold !important; text-transform: uppercase !important; letter-spacing: 1.5px !important; display: inline-block !important;';
      }
      if (iconSpan) {
        iconSpan.style.cssText = 'display: inline-flex !important; align-items: center !important; margin-right: 8px !important; flex-shrink: 0 !important; width: auto !important; height: auto !important;';
      }
    } else if (isFooter) {
      // Footer buttons: Best practice - visual state indication
      // Not paused: white background, black text, 2px border
      // Paused: light grey background, black text, 3px border, bold text (BEST PRACTICE)
      if (isPaused) {
        // Paused state: light grey background, thicker border, bold text
        button.style.setProperty('background-color', '#f0f0f0', 'important');
        button.style.setProperty('border', '3px solid #000000', 'important');
        button.style.setProperty('color', '#000000', 'important');
        button.style.setProperty('font-weight', 'bold', 'important');
      } else {
        // Not paused state: white background, normal border
        button.style.setProperty('background-color', '#ffffff', 'important');
        button.style.setProperty('border', '2px solid #000000', 'important');
        button.style.setProperty('color', '#000000', 'important');
        button.style.setProperty('font-weight', '500', 'important');
      }
      
      // Ensure button is always visible
      button.style.setProperty('opacity', '1', 'important');
      button.style.setProperty('visibility', 'visible', 'important');
      button.style.setProperty('display', 'inline-flex', 'important');
      button.style.setProperty('width', 'auto', 'important');
      button.style.setProperty('height', 'auto', 'important');
      button.style.setProperty('clip', 'auto', 'important');
      button.style.setProperty('position', 'relative', 'important');
      button.style.setProperty('left', 'auto', 'important');
      
      // Ensure text and icons are visible
      if (textSpan) {
        textSpan.style.setProperty('color', '#000000', 'important');
        textSpan.style.setProperty('opacity', '1', 'important');
        textSpan.style.setProperty('visibility', 'visible', 'important');
      }
      if (iconSpan) {
        iconSpan.style.setProperty('color', '#000000', 'important');
        iconSpan.style.setProperty('opacity', '1', 'important');
        iconSpan.style.setProperty('visibility', 'visible', 'important');
        const svg = iconSpan.querySelector('svg');
        if (svg) {
          svg.style.setProperty('fill', '#000000', 'important');
          svg.style.setProperty('color', '#000000', 'important');
        }
      }
    }
  }

  /**
   * Announce pause state change to screen readers
   */
  function announcePauseChange(paused) {
    const announcement = paused 
      ? 'All carousels have been paused'
      : 'All carousels have been resumed';
    
    // Create or update aria-live region
    let liveRegion = document.getElementById('ada-pause-announcements');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'ada-pause-announcements';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = announcement;
    
    // Clear after announcement
    setTimeout(() => {
      if (liveRegion) liveRegion.textContent = '';
    }, 1000);
  }

  /**
   * Initialize global pause control
   */
  function initGlobalPause() {
    // Load saved state
    isPaused = loadPauseState();
    
    // Create and insert pause button
    const pauseButton = createPauseButton('ada-global-pause-toggle');
    
    // Try to insert before contrast toggle, or in header
    const contrastToggle = document.getElementById('ada-accessibility-options');
    const header = document.querySelector('header') || document.body;
    
    if (contrastToggle && contrastToggle.parentNode) {
      contrastToggle.parentNode.insertBefore(pauseButton, contrastToggle.nextSibling);
    } else {
      // Try to find cart icon or other header element
      const cartIcon = document.querySelector('[data-cart-icon], .cart-icon, #cart-icon');
      if (cartIcon && cartIcon.parentNode) {
        cartIcon.parentNode.insertBefore(pauseButton, cartIcon.nextSibling);
      } else {
        // Fallback: append to header
        header.appendChild(pauseButton);
      }
    }

    registerPauseButton(pauseButton, { isPrimary: true });

    // Create mobile menu pause button if container exists
    const mmPauseTarget = document.querySelector('[data-mm-pause-target]');
    console.log('initGlobalPause: Looking for [data-mm-pause-target]', mmPauseTarget);
    if (mmPauseTarget) {
      const menuPauseButton = createPauseButton('ada-global-pause-toggle-mobile');
      menuPauseButton.setAttribute('data-context', 'mobile-menu');
      const labelId = mmPauseTarget.getAttribute('data-label-id');
      if (labelId) {
        menuPauseButton.setAttribute('aria-labelledby', labelId);
      }
      
      // CRITICAL: Apply all styles inline immediately to match login button EXACTLY - use same classes
      menuPauseButton.className = 'mm-button uppercase tracking-1_5px font-bold text-center border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-all duration-200';
      menuPauseButton.style.cssText = 'opacity: 1 !important; visibility: visible !important; display: flex !important; width: 100% !important; height: 44px !important; max-height: 44px !important; min-height: 44px !important; font-size: 14px !important; line-height: 1.2 !important; padding: 10px 16px !important; box-sizing: border-box !important; border-radius: 4px !important; background-color: #ffffff !important; border: 2px solid #000000 !important; color: #000000 !important; font-weight: bold !important; text-transform: uppercase !important; letter-spacing: 1.5px !important; align-items: center !important; justify-content: center !important; text-decoration: none !important; cursor: pointer !important;';
      
      registerPauseButton(menuPauseButton);
      mmPauseTarget.appendChild(menuPauseButton);
      console.log('initGlobalPause: Pause button created and appended', menuPauseButton);
    } else {
      console.log('initGlobalPause: [data-mm-pause-target] not found');
    }
    
    // Create footer pause button - always visible for discoverability
    const footerPauseTarget = document.querySelector('[data-footer-pause-target]');
    console.log('initGlobalPause: Looking for [data-footer-pause-target]', footerPauseTarget);
    if (footerPauseTarget) {
      const footerPauseButton = createPauseButton('ada-global-pause-toggle-footer');
      footerPauseButton.setAttribute('data-context', 'footer');
      footerPauseButton.classList.add('ada-footer-control'); // Add class for CSS targeting
      
      // CRITICAL: Ensure footer button is always visible
      footerPauseButton.style.setProperty('opacity', '1', 'important');
      footerPauseButton.style.setProperty('visibility', 'visible', 'important');
      footerPauseButton.style.setProperty('display', 'inline-flex', 'important');
      footerPauseButton.style.setProperty('width', 'auto', 'important');
      footerPauseButton.style.setProperty('height', 'auto', 'important');
      footerPauseButton.style.setProperty('clip', 'auto', 'important');
      footerPauseButton.style.setProperty('position', 'relative', 'important');
      footerPauseButton.style.setProperty('left', 'auto', 'important');
      
      registerPauseButton(footerPauseButton);
      footerPauseTarget.appendChild(footerPauseButton);
      console.log('initGlobalPause: Footer pause button created and appended', footerPauseButton);
    } else {
      console.log('initGlobalPause: [data-footer-pause-target] not found');
    }
    
    // Update button state
    updatePauseButtons();
    
    // Apply saved state
    if (isPaused) {
      pauseAllCarousels();
    }
    
    // Watch for new Swiper instances
    watchForSwiperInstances();
  }

  /**
   * Watch for dynamically added Swiper instances
   */
  function watchForSwiperInstances() {
    // Use MutationObserver to detect new Swiper instances
    const observer = new MutationObserver(function(mutations) {
      // Check for elements with Swiper classes
      const swiperElements = document.querySelectorAll('.swiper:not([data-pause-registered])');
      swiperElements.forEach(el => {
        // Try to find the Swiper instance
        if (el.swiper) {
          registerSwiperInstance(el.swiper);
          el.setAttribute('data-pause-registered', 'true');
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Also check existing Swiper elements
    setTimeout(() => {
      const existingSwipers = document.querySelectorAll('.swiper');
      existingSwipers.forEach(el => {
        if (el.swiper && !el.hasAttribute('data-pause-registered')) {
          registerSwiperInstance(el.swiper);
          el.setAttribute('data-pause-registered', 'true');
        }
      });
    }, 1000);
  }

  /**
   * Setup accessibility link in footer (similar to contrast control)
   */
  function setupAccessibilityLink() {
    function attachLinkHandler() {
      const link = document.getElementById('ada-pause-link');
      if (link) {
        link.addEventListener('click', function(e) {
          e.preventDefault();
          const toggle = primaryPauseButton || document.getElementById('ada-global-pause-toggle');
          if (toggle) {
            // Make toggle visible and focusable
            toggle.classList.add('ada-toggle-visible');
            toggle.scrollIntoView({ behavior: 'smooth', block: 'center' });
            toggle.focus();
          }
        });
        return true;
      }
      return false;
    }
    
    // Try immediately
    if (attachLinkHandler()) return;
    
    // Try on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        if (!attachLinkHandler()) {
          setTimeout(attachLinkHandler, 500);
        }
      });
    } else {
      setTimeout(attachLinkHandler, 500);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initGlobalPause();
      setupAccessibilityLink();
    });
  } else {
    initGlobalPause();
    setupAccessibilityLink();
  }

  // Expose API for sections to register their Swiper instances
  window.ADA_GlobalPause = {
    register: registerSwiperInstance,
    registerSwiper: registerSwiperInstance,
    unregister: unregisterSwiperInstance,
    registerCustom: registerCustomHandler,
    unregisterCustom: unregisterCustomHandler,
    pause: pauseAllCarousels,
    resume: resumeAllCarousels,
    isPaused: function() { return isPaused; }
  };

  try {
    document.dispatchEvent(new CustomEvent('ada-global-pause:ready'));
  } catch (e) {
    /* noop */
  }

})();

